import { Checkbox, Divider, Flex, Form, Input, Select, Space } from "@/atoms"
import { Heading } from "@/molecules/Heading"
import { InformationAlert } from "@/molecules/InformationAlert"
import { InformationPopover } from "@/molecules/InformationPopover"
import { ConfigurationBadge } from "@/organisms/Drawers/components/ConfigurationBadge/ConfigurationBadge"
import { CorrelationFieldsTemplate } from "@/organisms/Drawers/components/CorrelationFieldsTemplate"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ImageAutoComplete } from "./ImagesAutoComplete"

import styles from "./MonorepoConfigurationsForm.module.scss"

import type { MonorepoConfigurationsFormValues } from "./MonorepoConfigurationsForm.types"

const HelperLabel = (props: {
  label: string
  help: string
  isOptional?: boolean
}) => {
  const { t } = useTranslation()

  return (
    <Space>
      <span>{props.label}</span>
      <InformationPopover.Popover
        content={
          <InformationPopover.CommonTemplate>
            {props.help}
          </InformationPopover.CommonTemplate>
        }
      />
      {props.isOptional && (
        <span
          className={styles.textSecondary}
        >{`(${t("general.optional")})`}</span>
      )}
    </Space>
  )
}
export function MonorepoConfigurationsForm(props: {
  /**
   * Values
   */
  values: MonorepoConfigurationsFormValues
  /**
   * Called when change was made to any field
   */

  isRootSelected: boolean
  projectName: string
  onChange: (values: MonorepoConfigurationsFormValues) => void
  onResetSelected: () => void
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "inventory.codeRepositoriesTab.drawer.config"
  })
  const { values, isRootSelected } = props
  const {
    imageRepositories: selectedImageRepositories = [],
    splitPath = false,
    exclude = false,
    projectName
  } = values
  const correlations = useMemo(() => {
    return selectedImageRepositories.reduce(
      (acc, value) => {
        acc[value.imageRepository] = value.correlationType
        return acc
      },
      {} as Record<string, "auto" | "manual">
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  const handleChange = <
    K extends keyof MonorepoConfigurationsFormValues,
    V = MonorepoConfigurationsFormValues[K]
  >(
    field: K,
    newValue: V
  ) => {
    const values = {
      ...props.values,
      [field]: newValue
    }
    props.onChange(values)
  }

  const selected = (
    <Form.Item label={t("selected")}>
      <Select
        showSearch={false}
        mode="multiple"
        value={props.projectName}
        notFoundContent={null}
        onChange={(value) => {
          if (!value?.length) props.onResetSelected()
        }}
      />
    </Form.Item>
  )

  const notRootSelected = !isRootSelected

  return (
    <Flex vertical gap={16} className={styles.container}>
      <Form layout="vertical">
        <Flex vertical gap={36}>
          <Flex vertical gap={14}>
            {notRootSelected && (
              <>
                <InformationAlert
                  palette="info"
                  message={t("splitPathAlert")}
                />
                <Form.Item>
                  <Flex vertical gap={14}>
                    {selected}
                    <Checkbox
                      disabled={isRootSelected}
                      checked={splitPath}
                      onChange={(v) => {
                        handleChange("splitPath", v.target.checked)
                      }}
                    >
                      {t("splitLabel")}
                    </Checkbox>
                  </Flex>
                </Form.Item>
              </>
            )}
          </Flex>
          <CorrelationFieldsTemplate>
            <Flex vertical gap={24}>
              {isRootSelected && selected}
              {notRootSelected && (
                <Form.Item
                  label={
                    <HelperLabel
                      label={t("projectNameLabel")}
                      help={t("projectNameHelp")}
                    />
                  }
                >
                  <Input
                    disabled={!splitPath}
                    onChange={(e) =>
                      handleChange("projectName", e.target.value)
                    }
                    value={projectName}
                  />
                </Form.Item>
              )}
              <Form.Item
                label={
                  <HelperLabel
                    isOptional
                    label={t("imageCorrelationsLabel")}
                    help={t("imageCorrelationsHelp")}
                  />
                }
                help={
                  <Flex align="center" gap={8} className={styles.badgeWrapper}>
                    <span className="sm-text-ks">
                      {t("correlationDoneByLabel")}
                    </span>
                    <ConfigurationBadge
                      type="kodem"
                      textColor="secondary"
                      textSize="sm"
                    />
                    <ConfigurationBadge
                      type="manual"
                      textColor="secondary"
                      textSize="sm"
                    />
                  </Flex>
                }
              >
                <ImageAutoComplete
                  disabled={!splitPath && notRootSelected}
                  selectedImageRepositories={selectedImageRepositories}
                  value={selectedImageRepositories.map((v) => ({
                    value: v.imageRepository,
                    label: v.imageRepository,
                    ...v
                  }))}
                  onChange={(values) => {
                    if (!values) {
                      handleChange("imageRepositories", [])
                      return
                    }
                    handleChange(
                      "imageRepositories",
                      values.map((value) => ({
                        imageRepository: value,
                        correlationType: correlations[value] || "manual"
                      }))
                    )
                  }}
                />
              </Form.Item>
            </Flex>
          </CorrelationFieldsTemplate>
        </Flex>
        <Divider className={styles.divider} />
        <Flex vertical gap={16}>
          <Heading level={6} title={t("scanExclusionTitle")} />
          <Form.Item>
            <Checkbox
              disabled={!splitPath && notRootSelected}
              checked={exclude}
              onChange={(v) => {
                handleChange("exclude", v.target.checked)
              }}
            >
              {isRootSelected ? t("excludeRepoLabel") : t("excludeLabel")}
            </Checkbox>
          </Form.Item>
        </Flex>
      </Form>
    </Flex>
  )
}
